/*
  customsirge_recharge.js
  Purpose:  The one place the theme talks to Recharge's Storefront SDK. Both bundle
            builders — the PDP "Build your bundle" panel (customsirge_pdp_bundle.js)
            and the Build a Box page (customsirge_build_a_box.js) — load the SDK,
            read the bundle's rules and add the box to the cart through here, so
            they can never disagree about a minimum, a tier or a selling plan.
  SDK:      @rechargeapps/storefront-client 2.6.0, self-hosted as
            assets/customsirge_recharge_client.min.js and loaded on demand. Its
            URL comes from the caller (an asset_url printed by Liquid) because a
            theme asset's URL is only known server-side.
  Owns:     window.CustomsirgeRecharge — a plain library, not a registered
            component; the components that use it register themselves.
  Owner:    Sirge, feat/pdp-bundle-drawer

  WHAT 2.6.0 DOES AND DOES NOT DO (read from the build, 2026-10-16):
    * recharge.init() is synchronous and must run once per page.
    * recharge.cdn.getCDNBundleSettings(productId) returns the rules:
      variants[].ranges[].quantity_min/max, variants[].option_sources[]
      .collection_id, max_quantity_per_variant, and incentives
      .tiered_discounts[].tiers[].tier.{condition_quantity_gte, discount_type,
      discount_value}.
    * recharge.bundle.getDynamicBundleItems(bundle, handle) builds the cart
      lines (_rc_bundle, _rc_bundle_variant, _rc_bundle_parent,
      _rc_bundle_collection_id, selling_plan) and validates only that the box is
      not empty and its shipping intervals agree. It does NOT check the
      minimum, the maximum or the per-variant cap — the UI must, which is why
      settings() returns them.
*/

(function () {
  'use strict';

  if (window.CustomsirgeRecharge) return;

  var sdkPromise = null;
  var settingsCache = {};

  /* Load the vendored SDK once and initialise it once, however many builders
     ask. A failed load is forgotten so the next call can retry. */
  function load(store, src) {
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise(function (resolve, reject) {
      function ready() {
        try {
          if (!window.__customsirgeRechargeInit) {
            window.recharge.init({ storeIdentifier: store, appName: 'gigli-theme', appVersion: '1.0.0' });
            window.__customsirgeRechargeInit = true;
          }
          resolve(window.recharge);
        } catch (e) {
          reject(e);
        }
      }
      if (window.recharge && window.recharge.bundle) return ready();
      if (!src) return reject(new Error('Recharge SDK source missing'));
      var existing = document.querySelector('script[src="' + src + '"]');
      var s = existing || document.createElement('script');
      s.addEventListener('load', ready);
      s.addEventListener('error', function () { reject(new Error('Recharge SDK failed to load')); });
      if (!existing) {
        s.src = src;
        s.async = true;
        document.head.appendChild(s);
      }
    });
    sdkPromise.catch(function () { sdkPromise = null; });
    return sdkPromise;
  }

  /* The bundle's rules, normalised. Only published percentage tiers count —
     that is all Recharge applies to a dynamic bundle in this store. */
  function settings(rc, productId) {
    var key = String(productId);
    if (settingsCache[key]) return settingsCache[key];
    settingsCache[key] = rc.cdn.getCDNBundleSettings(key).then(function (st) {
      var variant = (st.variants || [])[0] || {};
      var range = (variant.ranges || [])[0] || {};
      var tiers = [];
      ((st.incentives && st.incentives.tiered_discounts) || []).forEach(function (t) {
        if (t.status && t.status !== 'published') return;
        /* Which line types the discount applies to: Recharge's eligible_line_item_types
           ("onetime", "subscription"). null = all. Gigli: the 3-pack 10% is one-time only. */
        var types = Array.isArray(t.eligible_line_item_types) ? t.eligible_line_item_types : null;
        (t.tiers || []).forEach(function (x) {
          var tier = x.tier || x;
          if (tier.discount_type !== 'percentage') return;
          tiers.push({ qty: Number(tier.condition_quantity_gte), pct: Number(tier.discount_value), types: types });
        });
      });
      tiers.sort(function (a, b) { return a.qty - b.qty; });
      var collectionIds = (variant.option_sources || [])
        .map(function (o) { return o.collection_id ? String(o.collection_id) : null; })
        .filter(Boolean);
      return {
        min: Number(range.quantity_min) || 1,
        max: range.quantity_max == null ? null : Number(range.quantity_max),
        maxPerVariant: st.max_quantity_per_variant == null ? null : Number(st.max_quantity_per_variant) || null,
        collectionIds: collectionIds,
        collectionId: collectionIds[0] || null,
        tiers: tiers,
      };
    });
    settingsCache[key].catch(function () { delete settingsCache[key]; });
    return settingsCache[key];
  }

  /* The Recharge collection a selection belongs to: the one the theme rendered
     the product from when Recharge lists it as an option source, otherwise the
     bundle's first option source. */
  function collectionFor(rules, renderedId) {
    var id = renderedId ? String(renderedId) : null;
    if (id && rules.collectionIds.indexOf(id) !== -1) return id;
    return rules.collectionId || id;
  }

  /* The tier % for a box of `count`. Pass lineType ('onetime' | 'subscription') to
     skip tiers Recharge doesn't apply to that kind of line; without it, every tier counts. */
  function percentAt(rules, count, lineType) {
    var pct = 0;
    if (!rules) return 0;
    rules.tiers.forEach(function (t) {
      if (lineType && t.types && t.types.indexOf(lineType) === -1) return;
      if (count >= t.qty) pct = t.pct;
    });
    return pct;
  }

  function money(cents, format) {
    var value = (Math.round(cents) / 100).toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*[a-z_]+\s*\}\}/, value);
    }
    return '$' + value;
  }

  /* A product's selling plans, as Liquid prints them on the item:
     "group name::plan id::% off" joined by "||" — one entry per PLAN. */
  function parsePlans(raw) {
    var plans = [];
    (raw || '').split('||').forEach(function (chunk) {
      var parts = chunk.split('::');
      if (parts.length < 2 || !parts[1]) return;
      plans.push({ group: parts[0], id: Number(parts[1]), pct: Number(parts[2] || 0) });
    });
    return plans;
  }

  /* The child product's plan for the bundle's chosen schedule. Same plan id
     first (Recharge shares the bundle's plans with its products), then the same
     group name — the two-step match Recharge documents for dynamic bundles. */
  function matchPlan(raw, choice) {
    if (!choice) return null;
    var plans = parsePlans(raw);
    var id = Number(choice.id);
    var byId = id ? plans.filter(function (p) { return p.id === id; })[0] : null;
    if (byId) return byId;
    return plans.filter(function (p) { return p.group === choice.name; })[0] || null;
  }

  /* Adds the box and, when Dawn's drawer is on the page, renders and opens it
     from the same response (sections + sections_url); otherwise goes to /cart. */
  function addBundle(opts) {
    var drawer = document.querySelector('cart-drawer');
    return load(opts.store, opts.src)
      .then(function (rc) { return rc.bundle.getDynamicBundleItems(opts.bundle, opts.handle); })
      .then(function (lines) {
        var body = { items: lines };
        if (drawer) {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }
        var url = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then(function (r) {
          return r.json().then(function (json) {
            if (!r.ok || json.status) throw new Error(json.description || json.message || 'Add to cart failed');
            return json;
          });
        });
      })
      .then(function (state) {
        if (drawer && typeof drawer.renderContents === 'function') {
          /* Dawn leaves .is-empty on the drawer from page load; the new lines stay hidden under it. */
          drawer.classList.remove('is-empty');
          var inner = drawer.querySelector('.drawer__inner');
          if (inner) inner.classList.remove('is-empty');
          drawer.renderContents(state);
          if (typeof drawer.open === 'function') drawer.open();
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
        return state;
      });
  }

  window.CustomsirgeRecharge = {
    load: load,
    settings: settings,
    collectionFor: collectionFor,
    percentAt: percentAt,
    money: money,
    parsePlans: parsePlans,
    matchPlan: matchPlan,
    addBundle: addBundle,
  };
})();
